import { useState, useCallback, useEffect } from "react";
import { List, BarChart3, Eye, AlertTriangle } from "../components/icons";
import { apiClient } from "../lib/api";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { RAIL_LAYOUT_QUERY } from "./logger/lib/cockpitRailQuery";
import HalftimePanel from "./logger/components/molecules/HalftimePanel";
import PenaltyShootoutPanel from "./logger/components/molecules/PenaltyShootoutPanel";
import SubstitutionFlow from "./logger/components/molecules/SubstitutionFlow";
import AnalyticsView from "./logger/components/organisms/AnalyticsView";
import LoggerView from "./logger/components/organisms/LoggerView";
import ReviewView from "./logger/components/organisms/ReviewView";
import CockpitTopSection from "./logger/components/organisms/CockpitTopSection";
import FloatingAiChat from "./logger/components/organisms/FloatingAiChat";
import { AI_INSIGHTS_ENABLED } from "../lib/featureFlags";
import { CockpitProvider, useCockpit } from "./logger/context/CockpitContext";
import { useLogIntegrityChecks } from "./logger/hooks/useLogIntegrityChecks";
import { copyTextToClipboard } from "../lib/clipboard";
import {
  preOpenSharePopup,
  runShareAnalyticsFlow,
  buildPublicMatchShareUrl,
  extractShareToken,
} from "../lib/shareAnalytics";
import { Match } from "./logger/types";
import { normalizeTeamId } from "../lib/teamPalette";

/**
 * PMA-COCK-106/109: full-screen block shown instead of the Logger Cockpit on
 * a phone-sized device (`isDeviceTooSmall`, see `useIsDeviceTooSmall` below)
 * — the client's own instruction was to block, not degrade, since a
 * mis-tapped event mid-match costs more than finding a tablet. No "continue
 * anyway" escape hatch, deliberately: recommended, not silently assumed (see
 * the PMA-COCK-106 changelog entry for the reasoning this decision is based
 * on — live-measured label/bolita overlap even at TABLET width is direct
 * evidence the interface does not degrade gracefully at phone width).
 * Reacts to resize/orientation change via `useIsDeviceTooSmall`'s own
 * `resize`/`orientationchange` subscription (the check lives in the parent,
 * not here) — this component itself is just the message, no logic.
 * Deliberately local to this file rather than a new file under the Guarded
 * File Set's own directory tree.
 */
function CockpitDeviceGate({ t }: { t: (key: string, opts?: any) => string }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-900 px-6"
      data-testid="cockpit-device-gate"
    >
      <div className="max-w-sm text-center flex flex-col items-center gap-4">
        <AlertTriangle
          size={40}
          className="text-amber-400"
          aria-hidden="true"
        />
        <h1 className="text-lg font-bold text-slate-100">
          {t("deviceGateTitle", {
            defaultValue: "Larger screen required",
          })}
        </h1>
        <p className="text-sm text-slate-400">
          {t("deviceGateMessage", {
            defaultValue:
              "The Logger Cockpit needs a tablet or larger screen to log events accurately. Please switch to a tablet or desktop device to continue.",
          })}
        </p>
      </div>
    </div>
  );
}

/**
 * PMA-COCK-109 (corrected): the smartphone-block threshold was originally
 * `min(window.innerWidth, window.innerHeight) < 700` (viewport-based, via
 * `useMediaQuery`) and it blocked real iPads in landscape. The viewport is
 * not the device: iPad Safari's toolbar/tab bar in landscape shave roughly
 * 70-120px off the visible viewport height, pushing `innerHeight` from the
 * nominal 768 down to ~650-700 — under the 700 line even though the device
 * itself clears it comfortably. Portrait survived only because its `min` is
 * the width axis (768px), which browser chrome does not reduce.
 *
 * Fix: gate on the PHYSICAL SCREEN (`window.screen.width`/`.height`)
 * instead — these report the device's CSS-px screen size and are not
 * affected by browser chrome, so they can't drift the way the viewport
 * does. `min()` is orientation-invariant by construction (it's always the
 * device's short axis), so — unlike the viewport rule — this one needs no
 * per-orientation reasoning about which axis chrome eats into.
 *
 * Threshold 600 (down from 700, re-verified against the same device set the
 * original threshold used): iPad mini (6th gen, the smallest tablet in
 * common use) is 744 on its short side — clears 600 by 144px. The largest
 * common phones/phablets (iPhone 15/16 Pro Max, Galaxy S23/24 Ultra) land at
 * min≈412-430 — 170-188px under the line. Both margins are far wider than
 * any plausible browser-chrome effect, so there is no equivalent
 * close-to-the-line trap here.
 *
 * Fails OPEN: an unavailable `window.screen`, a non-finite value, or either
 * axis below `MIN_PLAUSIBLE_SCREEN_DIMENSION` (a broken/stubbed environment,
 * not a real device) renders the cockpit rather than blocking it — blocking
 * a legitimate user is far worse than failing to block a phone, and that
 * asymmetry is the whole lesson of this bug. Exported for unit testing
 * (mirrors `isKickoffAttemptStatus`'s pattern in `CockpitContext.tsx`).
 */
export const DEVICE_TOO_SMALL_THRESHOLD = 600;
const MIN_PLAUSIBLE_SCREEN_DIMENSION = 50;

export function computeIsDeviceTooSmall(
  screenWidth: number | null | undefined,
  screenHeight: number | null | undefined,
): boolean {
  if (
    typeof screenWidth !== "number" ||
    typeof screenHeight !== "number" ||
    !Number.isFinite(screenWidth) ||
    !Number.isFinite(screenHeight) ||
    screenWidth < MIN_PLAUSIBLE_SCREEN_DIMENSION ||
    screenHeight < MIN_PLAUSIBLE_SCREEN_DIMENSION
  ) {
    return false;
  }
  return Math.min(screenWidth, screenHeight) < DEVICE_TOO_SMALL_THRESHOLD;
}

function readScreenDimensions(): {
  width: number | null;
  height: number | null;
} {
  if (typeof window === "undefined" || !window.screen) {
    return { width: null, height: null };
  }
  return { width: window.screen.width, height: window.screen.height };
}

/**
 * Reacts to resize/orientation change, not just first paint — mirrors
 * `useMediaQuery`'s subscription model, but listens on `resize`/
 * `orientationchange` directly since `window.screen` has no native
 * `matchMedia`-style change event of its own.
 */
export function useIsDeviceTooSmall(): boolean {
  const [isTooSmall, setIsTooSmall] = useState<boolean>(() => {
    const { width, height } = readScreenDimensions();
    return computeIsDeviceTooSmall(width, height);
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const recompute = () => {
      const { width, height } = readScreenDimensions();
      setIsTooSmall(computeIsDeviceTooSmall(width, height));
    };
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("orientationchange", recompute);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("orientationchange", recompute);
    };
  }, []);

  return isTooSmall;
}

export default function LoggerCockpit() {
  return (
    <CockpitProvider>
      <CockpitContent />
    </CockpitProvider>
  );
}

// ---------------------------------------------------------------------------
// Inner component – consumes the cockpit context and renders views.
// ---------------------------------------------------------------------------
function CockpitContent() {
  const {
    t,
    isLoggerReady,
    matchId,
    match,
    setMatch,
    loading,
    error,
    isAdmin,
    isConnected,
    queuedCount,
    failedCount,
    pendingAckCount,
    isSubmitting,
    openResetModal,
    resetBlocked,
    resetTooltip,
    undoError,
    viewMode,
    setViewMode,
    statusOverride,
    matchForPhase,
    showResetModal,
    setShowResetModal,
    isCompletedOrFulltime,
    resetDisabledReason,
    resetConfirmText,
    setResetConfirmText,
    confirmGlobalReset,
    ineffectiveNoteOpen,
    ineffectiveActionType,
    ineffectiveTeamSelection,
    ineffectiveActionDropdownOpen,
    ineffectiveTeamDropdownOpen,
    ineffectiveNoteText,
    manualHomeTeamLabel,
    manualAwayTeamLabel,
    isMatchLive,
    setIneffectiveActionDropdownOpen,
    setIneffectiveTeamDropdownOpen,
    setIneffectiveActionType,
    setIneffectiveTeamSelection,
    setIneffectiveNoteText,
    cancelIneffectiveNote,
    confirmIneffectiveNote,
    liveScore,
    transitionError,
    toast,
    currentStatusNormalized,
    currentPhase,
    clockMode,
    isGlobalClockRunning,
    cockpitLocked,
    lockReason,
    showExtraTimeAlert,
    periodInfo,
    broadcastClock,
    transitionToHalftime,
    transitionToFulltime,
    dismissExtraTimeAlert,
    showDriftNudge,
    driftSeconds,
    fetchMatch,
    operatorPeriod,
    globalClock,
    broadcastMatchClock,
    guardTransition,
    transitionToSecondHalf,
    transitionToExtraFirst,
    transitionToExtraHalftime,
    transitionToExtraSecond,
    transitionToPenalties,
    finishMatch,
    transitionDisabled,
    penaltiesTransitionDisabled,
    transitionReason,
    duplicateStats,
    lastDuplicateSummaryDetails,
    lastDuplicateSummaryDefault,
    lastDuplicateTeamName,
    lastDuplicateSeenAt,
    resetDuplicateStats,
    duplicateHighlight,
    duplicateDetailsDefault,
    duplicateSessionDetailsSuffix,
    duplicateSessionSummaryDefault,
    duplicateExistingEventDefault,
    clearDuplicateHighlight,
    dismissToast,
    effectiveClock,
    ineffectiveClock,
    varTimeClock,
    timeoutTimeClock,
    effectiveTime,
    varTimeSeconds,
    timeoutTimeSeconds,
    ineffectiveBreakdown,
    isVarActive,
    isTimeoutActive,
    liveEvents,
    queuedEvents,
    buffer,
    handleGlobalClockStartGuarded,
    handleGlobalClockStopGuarded,
    handleModeSwitchGuarded,
    handleVarToggle,
    handleTimeoutToggle,
    handleRefereeAction,
    showFieldResume,
    hasActiveIneffective,
    currentStep,
    selectedPlayer,
    selectedAction,
    pendingCardType,
    fieldAnchor,
    onFieldIds,
    shootout,
    logShootoutKick,
    selectedTeam,
    expelledPlayerIds,
    cardDisciplinaryStatus,
    goalCountByPlayerId,
    setSelectedTeam,
    handlePlayerSelection,
    handleFieldPlayerSelection,
    handleFieldDestination,
    handleZoneSelect,
    handleQuickActionSelect,
    handleOpenMoreActions,
    resetFlow,
    handleQuickSubstitution,
    handleCardSelection,
    cancelCardSelection,
    availableActions,
    handleActionClickOverride,
    availableOutcomes,
    handleOutcomeSelect,
    currentTeam,
    eligibleRecipients,
    handleRecipientSelect,
    handleUndoLastEvent,
    undoDisabled,
    undoCount,
    manualFieldFlip,
    setManualFieldFlip,
    priorityPlayerId,
    handleDeletePendingEvent,
    handleDeleteLoggedEvent,
    handleUpdateEventNotes,
    handleUpdateEventData,
    handleVarCornerToGoalKick,
    handleUpdateGoalPlacement,
    getDisplayPosition,
    handleTacticalPlayerDragEnd,
    draggingPlayerId,
    setDraggingPlayerId,
    allTacticalPositions,
    homeFormation,
    awayFormation,
    applyFormation,
    positionMode,
    setPositionMode,
    showSubstitutionFlow,
    substitutionModalTeam,
    substitutionAvailablePlayers,
    substitutionSquadPlayers,
    substitutionOnField,
    substitutionIsSubmittingSquadPlayer,
    substitutionSquadSubmitError,
    substitutionInitialPlayerOn,
    substitutionInitialPlayerOff,
    handleSubstitutionSubmit,
    handleSubstitutionCancel,
  } = useCockpit();

  const { nudges, dismissNudge } = useLogIntegrityChecks(
    liveEvents,
    queuedEvents,
  );

  const homeTeamColorInfo = match?.home_team
    ? {
        teamId: normalizeTeamId(
          match.home_team.team_id ?? match.home_team.id ?? "",
        ),
        primary_color: match.home_team.primary_color ?? null,
        secondary_color: match.home_team.secondary_color ?? null,
      }
    : undefined;

  const awayTeamColorInfo = match?.away_team
    ? {
        teamId: normalizeTeamId(
          match.away_team.team_id ?? match.away_team.id ?? "",
        ),
        primary_color: match.away_team.primary_color ?? null,
        secondary_color: match.away_team.secondary_color ?? null,
      }
    : undefined;

  const handleTeamColorSaved = useCallback(
    (teamId: string, primary: string | null, secondary: string | null) => {
      setMatch((prev: Match | null) => {
        if (!prev) return null;
        const patchSide = (team: Match["home_team"]) =>
          normalizeTeamId(team?.team_id ?? team?.id ?? "") === teamId
            ? { ...team, primary_color: primary, secondary_color: secondary }
            : team;
        return {
          ...prev,
          home_team: patchSide(prev.home_team),
          away_team: patchSide(prev.away_team),
        };
      });
    },
    [setMatch],
  );

  const [shareCopied, setShareCopied] = useState(false);
  const [shareFeedback, setShareFeedback] = useState<{
    tone: "error" | "info";
    message: string;
  } | null>(null);

  /** PMA-COCK-106: true at the landscape-tablet breakpoint the client's iPad
   * (1024x768) falls into. Drives `LoggerView.tsx`'s two-column rail —
   * this is the 3rd scope revision. v1 (withdrawn before shipping) put
   * ScoreBoard+MatchTimerDisplay+TeamSelector+RefereeActionBar all in the
   * rail: measured live at 881px bottom-of-pitch, but rail content (777px)
   * exceeded its window (608px) with no scroll affordance. v2 (shipped as
   * 555a0b5, since superseded) pulled the rail back to RefereeActionBar
   * only: 1328px bottom-of-pitch, no fold, but ~300px of the rail column
   * sat visibly empty — recreating the client's own "blank spot" complaint
   * rather than resolving it. v3 (this): ScoreBoard + MatchTimerDisplay +
   * RefereeActionBar in the rail; TeamSelector deliberately excluded and
   * stays stacked above the pitch in BOTH layouts — its formation pickers
   * are inherently two-sided, built to sit near their own half of the
   * pitch, a structural mismatch with a one-sided rail regardless of
   * space, confirmed rather than assumed once v1 put it there anyway.
   * Portrait (768x1024) does NOT match — `orientation: landscape` excludes
   * it deliberately, see team_findings.md for why portrait has no dead
   * width to give a rail (768px pitch there is natural-width-bound, not
   * vh-formula-bound). Desktop (>=1180px) also does not match, by the
   * `max-width` term. */
  // EPIC-111 UIJ-R1-1: large-iPad viewports (1024x1366 portrait, 1366x1024
  // landscape) also get the rail; 1280x720 / 1440x900 desktops stay stacked.
  const isRailLayout = useMediaQuery(RAIL_LAYOUT_QUERY);
  // EPIC-111 UIJ-R2-1: portrait tablets (< 1024px wide, no rail) move the
  // period ladder, referee actions and step hint below the pitch.
  const isNarrowViewport = useMediaQuery("(max-width: 1023px)");
  const isPortraitStack = isNarrowViewport && !isRailLayout;

  /** PMA-COCK-109: `min(window.screen.width, window.screen.height) < 600`,
   * gating on the physical screen rather than the viewport — see
   * `useIsDeviceTooSmall`'s own doc comment above for the full rationale
   * (this replaced a viewport-based rule that blocked real iPads in
   * landscape) and `computeIsDeviceTooSmall` for the fail-open behavior. */
  const isDeviceTooSmall = useIsDeviceTooSmall();

  const handleShare = useCallback(async () => {
    if (!match) return;
    setShareFeedback(null);
    const identifier = (match as any)._id || matchId;

    if (match.share_token) {
      const url = buildPublicMatchShareUrl(
        window.location.origin,
        match.share_token,
      );
      try {
        window.open(url, "_blank");
        void copyTextToClipboard(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch (err) {
        console.error("Sync share failed:", err);
      }
      return;
    }

    const popupWindow = preOpenSharePopup();

    const result = await runShareAnalyticsFlow({
      origin: window.location.origin,
      popupWindow,
      requestShareToken: async () => {
        const payload = await apiClient.post(`/matches/${identifier}/share`);
        const token = extractShareToken(payload);
        if (token) {
          setMatch((prev: Match | null) =>
            prev ? { ...prev, share_token: token } : null,
          );
        }
        return payload;
      },
      copyToClipboard: (url) => copyTextToClipboard(url),
    });

    if (result.status === "error") {
      setShareCopied(false);
      setShareFeedback({
        tone: "error",
        message: t("shareAnalyticsFailed", {
          ns: "logger",
          defaultValue:
            "Unable to share analytics right now. Please try again.",
        }),
      });
      setTimeout(() => setShareFeedback(null), 3000);
      console.error("Share failed:", result.error ?? result.reason);
      return;
    }

    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);

    if (!result.popupOpened) {
      setShareFeedback({
        tone: "info",
        message: t("sharePopupBlockedCopied", {
          ns: "logger",
          defaultValue: "Popup blocked. Share link copied to clipboard.",
        }),
      });
      setTimeout(() => setShareFeedback(null), 3000);
    }
  }, [match, matchId, t, setMatch]);

  if (!isLoggerReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-4 border-slate-700 border-t-primary-500 animate-spin" />
          <div className="text-sm text-slate-400">
            Loading logger interface…
          </div>
        </div>
      </div>
    );
  }

  /* PMA-COCK-106/109: block the Logger Cockpit on phone-sized devices — the
   * client's own words, "basically impossible to use in those resolutions."
   * `isDeviceTooSmall` is computed unconditionally with the other hooks
   * above, so hook order never depends on loading state; the check itself
   * is placed after the `isLoggerReady` guard (not before) so `t()` has a
   * loaded "logger" namespace and the block screen's message is genuinely
   * localized on first paint, not an English fallback. Still checked before
   * `loading`/`error` below — the block is purely screen-size-driven and
   * shouldn't wait on match/auth data. The public share/analytics page
   * (`PublicMatchAnalytics.tsx`) is a structurally separate route component
   * this gate never touches — it stays fully usable on a phone. */
  if (isDeviceTooSmall) {
    return <CockpitDeviceGate t={t} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-4 border-slate-700 border-t-primary-500 animate-spin" />
          <div className="text-sm text-slate-400">{t("loading")}</div>
        </div>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-sm text-red-400">
          {error || t("errorLoadingMatch")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <div
        className="w-full max-w-[2200px] mx-auto xl:grid xl:grid-cols-[minmax(140px,1fr)_minmax(0,7.5fr)_minmax(140px,1fr)] 2xl:grid-cols-[minmax(180px,1fr)_minmax(0,8fr)_minmax(180px,1fr)]"
        data-testid="logger-page-shell"
      >
        <aside
          className="hidden xl:block border-r border-slate-800/80"
          data-testid="logger-shell-left"
          aria-hidden="true"
        />

        <div className="min-w-0" data-testid="logger-shell-center">
          <CockpitTopSection
            t={t}
            match={match}
            isConnected={isConnected}
            queuedCount={queuedCount}
            failedCount={failedCount}
            pendingAckCount={pendingAckCount}
            onFieldIds={onFieldIds}
            isSubmitting={isSubmitting}
            isAdmin={isAdmin}
            openResetModal={openResetModal}
            resetBlocked={resetBlocked}
            resetTooltip={resetTooltip}
            undoError={undoError}
            viewMode={viewMode}
            setViewMode={setViewMode}
            statusOverride={statusOverride}
            showResetModal={showResetModal}
            setShowResetModal={setShowResetModal}
            isCompletedOrFulltime={isCompletedOrFulltime}
            resetDisabledReason={resetDisabledReason}
            resetConfirmText={resetConfirmText}
            setResetConfirmText={setResetConfirmText}
            confirmGlobalReset={confirmGlobalReset}
            ineffectiveNoteOpen={ineffectiveNoteOpen}
            ineffectiveActionType={ineffectiveActionType}
            ineffectiveTeamSelection={ineffectiveTeamSelection}
            ineffectiveActionDropdownOpen={ineffectiveActionDropdownOpen}
            ineffectiveTeamDropdownOpen={ineffectiveTeamDropdownOpen}
            ineffectiveNoteText={ineffectiveNoteText}
            manualHomeTeamLabel={manualHomeTeamLabel}
            manualAwayTeamLabel={manualAwayTeamLabel}
            setIneffectiveActionDropdownOpen={setIneffectiveActionDropdownOpen}
            setIneffectiveTeamDropdownOpen={setIneffectiveTeamDropdownOpen}
            setIneffectiveActionType={setIneffectiveActionType}
            setIneffectiveTeamSelection={setIneffectiveTeamSelection}
            setIneffectiveNoteText={setIneffectiveNoteText}
            cancelIneffectiveNote={cancelIneffectiveNote}
            confirmIneffectiveNote={confirmIneffectiveNote}
            liveScore={liveScore}
            transitionError={transitionError}
            toast={toast}
            currentStatusNormalized={currentStatusNormalized}
            currentPhase={currentPhase}
            clockMode={clockMode}
            isGlobalClockRunning={isGlobalClockRunning}
            cockpitLocked={cockpitLocked}
            lockReason={lockReason}
            showExtraTimeAlert={showExtraTimeAlert}
            extraTimeSeconds={periodInfo.extraTimeSeconds}
            transitionToHalftime={transitionToHalftime}
            transitionToFulltime={transitionToFulltime}
            dismissExtraTimeAlert={dismissExtraTimeAlert}
            showDriftNudge={showDriftNudge}
            driftSeconds={driftSeconds}
            fetchMatch={fetchMatch}
            operatorPeriod={operatorPeriod}
            isExtraTime={periodInfo.isExtraTime}
            globalClock={globalClock}
            guardTransition={guardTransition}
            transitionToSecondHalf={transitionToSecondHalf}
            transitionToExtraFirst={transitionToExtraFirst}
            transitionToExtraHalftime={transitionToExtraHalftime}
            transitionToExtraSecond={transitionToExtraSecond}
            transitionToPenalties={transitionToPenalties}
            finishMatch={finishMatch}
            transitionDisabled={transitionDisabled}
            penaltiesTransitionDisabled={penaltiesTransitionDisabled}
            transitionReason={transitionReason}
            duplicateStats={duplicateStats}
            lastDuplicateSummaryDetails={lastDuplicateSummaryDetails}
            lastDuplicateSummaryDefault={lastDuplicateSummaryDefault}
            lastDuplicateTeamName={lastDuplicateTeamName}
            lastDuplicateSeenAt={lastDuplicateSeenAt}
            resetDuplicateStats={resetDuplicateStats}
            duplicateHighlight={duplicateHighlight}
            duplicateDetailsDefault={duplicateDetailsDefault}
            duplicateSessionDetailsSuffix={duplicateSessionDetailsSuffix}
            duplicateSessionSummaryDefault={duplicateSessionSummaryDefault}
            duplicateExistingEventDefault={duplicateExistingEventDefault}
            clearDuplicateHighlight={clearDuplicateHighlight}
            dismissToast={dismissToast}
            onShare={handleShare}
            shareCopied={shareCopied}
            hideScoreBoard={isRailLayout}
            hideMatchPeriodSelector={isRailLayout || isPortraitStack}
          />

          {shareFeedback && (
            <div
              role={shareFeedback.tone === "error" ? "alert" : "status"}
              data-testid="share-feedback"
              className={`px-4 sm:px-6 xl:px-8 2xl:px-10 py-3 text-sm border-b ${
                shareFeedback.tone === "error"
                  ? "border-red-700/60 bg-red-900/30 text-red-300"
                  : "border-amber-700/60 bg-amber-900/20 text-amber-100"
              }`}
            >
              {shareFeedback.message}
            </div>
          )}

          {/* Integrity nudges (PMA-COCK-051) — dismissible, visual-only, no event mutation */}
          {nudges.length > 0 && (
            <div
              data-testid="integrity-nudges"
              className="px-4 sm:px-6 xl:px-8 2xl:px-10 py-2 space-y-1.5"
            >
              {nudges.map((nudge) => (
                <div
                  key={nudge.id}
                  role="alert"
                  data-testid={`integrity-nudge-${nudge.rule}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-amber-700/50 bg-amber-900/20 px-4 py-2 text-sm text-amber-200"
                >
                  <span>
                    {nudge.rule === "shot-without-buildup" &&
                      t("integrityNudges.shotWithoutBuildup", {
                        ns: "logger",
                        defaultValue: `Shot at ${nudge.matchClock} — no preceding build-up detected in the opening seconds.`,
                        clock: nudge.matchClock,
                      })}
                    {nudge.rule === "missing-expulsion" &&
                      t("integrityNudges.missingExpulsion", {
                        ns: "logger",
                        defaultValue: `Red card (player ${nudge.playerId}) — no substitution logged in the next 5 events.`,
                        player: nudge.playerId,
                      })}
                    {nudge.rule === "substitution-window" &&
                      t("integrityNudges.substitutionWindow", {
                        ns: "logger",
                        defaultValue: `Substitution at period ${nudge.period} — outside allowed match periods (1–4).`,
                        period: nudge.period,
                      })}
                  </span>
                  <button
                    type="button"
                    onClick={() => dismissNudge(nudge.id)}
                    aria-label={t("integrityNudges.dismiss", {
                      ns: "logger",
                      defaultValue: "Dismiss",
                    })}
                    className="shrink-0 text-xs font-semibold uppercase tracking-wide text-amber-400 hover:text-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                  >
                    {t("integrityNudges.dismiss", {
                      ns: "logger",
                      defaultValue: "Dismiss",
                    })}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Main Content */}
          {/* PMA-COCK-121 (2026-08-16): px-4 sm:px-6 -> max-[1180px]:landscape:px-2
              reclaims the page-edge horizontal padding at iPad landscape, the
              last leg of the pitch-enlargement reclaim (client ask: "make
              bigger the soccer field"). Horizontal only — py-6 md:py-4 is
              untouched, load-bearing for the fold. Desktop (>=1180px) and
              portrait keep px-4 sm:px-6 unchanged. */}
          <main
            className="w-full max-w-none px-4 sm:px-6 max-[1180px]:landscape:px-2 xl:px-8 2xl:px-10 py-6 md:py-4"
            data-testid="cockpit-main"
          >
            {buffer && (
              <div
                data-testid="keyboard-buffer"
                className="fixed bottom-6 right-6 bg-slate-800 border border-slate-600 text-white px-4 py-3 rounded-xl shadow-xl z-50"
              >
                <div className="text-xs uppercase tracking-wide text-slate-400 leading-none mb-1">
                  Input
                </div>
                <div
                  className="text-3xl font-mono font-bold tabular-nums text-white"
                  data-testid="keyboard-buffer-value"
                >
                  {buffer}
                </div>
              </div>
            )}

            {/* Halftime Panel */}
            {currentPhase === "HALFTIME" && (
              <div className="mb-6">
                <HalftimePanel
                  onStartSecondHalf={transitionToSecondHalf}
                  scopedStoppageOpen={isVarActive || isTimeoutActive}
                  t={t}
                />
              </div>
            )}

            {/* Penalty shootout. Rendered above the view switcher so it is
                the first thing on screen for the whole PENALTIES phase — the
                operator has nothing else to log at that point, and the phase
                previously left them with a running cockpit and no way to
                record who scored or missed. */}
            {currentPhase === "PENALTIES" && (
              <div className="mb-6">
                <PenaltyShootoutPanel
                  match={match}
                  state={shootout}
                  onFieldIds={new Set([...onFieldIds.home, ...onFieldIds.away])}
                  disabled={cockpitLocked}
                  onLogKick={logShootoutKick}
                  onUndoLastKick={handleUndoLastEvent}
                  t={t}
                />
              </div>
            )}

            {/* Analytics View */}
            {viewMode === "analytics" ? (
              <>
                {/* Segmented toggle to return to logger — mirrors TeamSelector control */}
                <div className="flex justify-end mb-2">
                  <div className="flex rounded-xl border border-slate-600 overflow-hidden">
                    <button
                      type="button"
                      data-testid="toggle-logger"
                      onClick={() => setViewMode("logger")}
                      className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors text-slate-400 hover:text-slate-200 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 min-h-[44px]"
                    >
                      <List size={16} aria-hidden="true" />
                      {t("logger:logger.view", "Logger")}
                    </button>
                    <button
                      type="button"
                      data-testid="toggle-review-from-alt"
                      onClick={() => setViewMode("review")}
                      className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors text-slate-400 hover:text-slate-200 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 min-h-[44px]"
                    >
                      <Eye size={16} aria-hidden="true" />
                      {t("logger:logger.review", "Review")}
                    </button>
                    <button
                      type="button"
                      data-testid="toggle-analytics"
                      onClick={() => setViewMode("logger")}
                      className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors bg-primary-600 text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 min-h-[44px]"
                    >
                      <BarChart3 size={16} aria-hidden="true" />
                      {t("logger:logger.analytics", "Analytics")}
                    </button>
                  </div>
                </div>
                <AnalyticsView
                  match={match}
                  liveEvents={liveEvents}
                  queuedEvents={queuedEvents}
                  onFieldIds={onFieldIds}
                  effectiveClock={effectiveClock}
                  globalClock={globalClock}
                  broadcastMatchClock={broadcastMatchClock}
                  currentPeriod={operatorPeriod}
                  matchFinished={
                    currentPhase === "COMPLETED" || currentPhase === "PENALTIES"
                  }
                  ineffectiveClock={ineffectiveClock}
                  varClock={varTimeClock}
                  timeoutClock={timeoutTimeClock}
                  effectiveTime={effectiveTime}
                  varTimeSeconds={varTimeSeconds}
                  timeoutTimeSeconds={timeoutTimeSeconds}
                  ineffectiveBreakdown={ineffectiveBreakdown}
                  clockMode={clockMode}
                  isVarActive={isVarActive}
                  isTimeoutActive={isTimeoutActive}
                  t={t}
                />
              </>
            ) : viewMode === "review" ? (
              <>
                {/* Segmented toggle for review view */}
                <div className="flex justify-end mb-2">
                  <div className="flex rounded-xl border border-slate-600 overflow-hidden">
                    <button
                      type="button"
                      data-testid="toggle-logger"
                      onClick={() => setViewMode("logger")}
                      className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors text-slate-400 hover:text-slate-200 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 min-h-[44px]"
                    >
                      <List size={16} aria-hidden="true" />
                      {t("logger:logger.view", "Logger")}
                    </button>
                    <button
                      type="button"
                      data-testid="toggle-review"
                      onClick={() => setViewMode("review")}
                      className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors bg-primary-600 text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 min-h-[44px]"
                    >
                      <Eye size={16} aria-hidden="true" />
                      {t("logger:logger.review", "Review")}
                    </button>
                    <button
                      type="button"
                      data-testid="toggle-analytics-from-review"
                      onClick={() => setViewMode("analytics")}
                      className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold transition-colors text-slate-400 hover:text-slate-200 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 min-h-[44px]"
                    >
                      <BarChart3 size={16} aria-hidden="true" />
                      {t("logger:logger.analytics", "Analytics")}
                    </button>
                  </div>
                </div>
                <ReviewView
                  match={match}
                  liveEvents={liveEvents}
                  queuedEvents={queuedEvents}
                  isAdmin={isAdmin}
                  onUpdateEventData={handleUpdateEventData}
                  onUpdateEventNotes={handleUpdateEventNotes}
                  onDeleteEvent={isAdmin ? handleDeleteLoggedEvent : undefined}
                  onVarCornerToGoalKick={handleVarCornerToGoalKick}
                  onUpdateGoalPlacement={handleUpdateGoalPlacement}
                  onDeletePendingEvent={handleDeletePendingEvent}
                  expelledPlayerIds={expelledPlayerIds}
                  t={t}
                />
              </>
            ) : (
              <LoggerView
                match={match}
                matchForPhase={matchForPhase}
                operatorPeriod={operatorPeriod}
                globalClock={globalClock}
                broadcastMatchClock={broadcastMatchClock}
                effectiveClock={effectiveClock}
                ineffectiveClock={ineffectiveClock}
                varTimeClock={varTimeClock}
                timeoutTimeClock={timeoutTimeClock}
                clockMode={clockMode}
                isGlobalClockRunning={isGlobalClockRunning}
                cockpitLocked={cockpitLocked}
                lockReason={lockReason}
                handleGlobalClockStartGuarded={handleGlobalClockStartGuarded}
                handleGlobalClockStopGuarded={handleGlobalClockStopGuarded}
                handleModeSwitchGuarded={handleModeSwitchGuarded}
                handleVarToggle={handleVarToggle}
                handleTimeoutToggle={handleTimeoutToggle}
                handleRefereeAction={handleRefereeAction}
                isVarActive={isVarActive}
                isTimeoutActive={isTimeoutActive}
                showFieldResume={showFieldResume}
                isHalftimePhase={
                  currentPhase === "HALFTIME" ||
                  currentPhase === "EXTRA_HALFTIME"
                }
                hasActiveIneffective={hasActiveIneffective}
                ineffectiveTeamLabel={
                  ineffectiveTeamSelection === "home"
                    ? (match?.away_team?.short_name ?? "Away")
                    : (match?.home_team?.short_name ?? "Home")
                }
                broadcastMainClock={broadcastClock.mainClock}
                broadcastAddedClock={broadcastClock.addedClock}
                currentStep={currentStep}
                selectedPlayer={selectedPlayer}
                selectedAction={selectedAction}
                pendingCardType={pendingCardType}
                fieldAnchor={fieldAnchor}
                onFieldIds={onFieldIds}
                selectedTeam={selectedTeam}
                expelledPlayerIds={expelledPlayerIds}
                cardDisciplinaryStatus={cardDisciplinaryStatus}
                goalCountByPlayerId={goalCountByPlayerId}
                setSelectedTeam={setSelectedTeam}
                handlePlayerSelection={handlePlayerSelection}
                handleFieldPlayerSelection={handleFieldPlayerSelection}
                handleFieldDestination={handleFieldDestination}
                handleZoneSelect={handleZoneSelect}
                handleQuickActionSelect={handleQuickActionSelect}
                handleOpenMoreActions={handleOpenMoreActions}
                resetFlow={resetFlow}
                handleQuickSubstitution={handleQuickSubstitution}
                handleCardSelection={handleCardSelection}
                cancelCardSelection={cancelCardSelection}
                availableActions={availableActions}
                isSubmitting={isSubmitting}
                handleActionClickOverride={handleActionClickOverride}
                availableOutcomes={availableOutcomes}
                handleOutcomeSelect={handleOutcomeSelect}
                currentTeam={currentTeam}
                eligibleRecipients={eligibleRecipients}
                handleRecipientSelect={handleRecipientSelect}
                handleUndoLastEvent={handleUndoLastEvent}
                undoDisabled={undoDisabled}
                undoCount={undoCount}
                manualFieldFlip={manualFieldFlip}
                setManualFieldFlip={setManualFieldFlip}
                priorityPlayerId={priorityPlayerId}
                liveEvents={liveEvents}
                duplicateHighlight={duplicateHighlight}
                handleDeletePendingEvent={handleDeletePendingEvent}
                handleDeleteLoggedEvent={handleDeleteLoggedEvent}
                isAdmin={isAdmin}
                handleUpdateEventNotes={handleUpdateEventNotes}
                handleUpdateEventData={handleUpdateEventData}
                handleVarCornerToGoalKick={handleVarCornerToGoalKick}
                handleUpdateGoalPlacement={handleUpdateGoalPlacement}
                getDisplayPosition={getDisplayPosition}
                onTacticalPlayerDragEnd={handleTacticalPlayerDragEnd}
                draggingPlayerId={draggingPlayerId}
                onTacticalDragStart={setDraggingPlayerId}
                onTacticalDragStop={() => setDraggingPlayerId(null)}
                allTacticalPositions={allTacticalPositions}
                homeFormation={homeFormation}
                awayFormation={awayFormation}
                applyFormation={applyFormation}
                viewMode={viewMode}
                setViewMode={setViewMode}
                homeTeamColorInfo={homeTeamColorInfo}
                awayTeamColorInfo={awayTeamColorInfo}
                onTeamColorSaved={handleTeamColorSaved}
                isMatchLive={isMatchLive}
                positionMode={positionMode}
                onPositionModeChange={setPositionMode}
                queuedEvents={queuedEvents}
                effectiveTime={effectiveTime}
                varTimeSeconds={varTimeSeconds}
                timeoutTimeSeconds={timeoutTimeSeconds}
                ineffectiveBreakdown={ineffectiveBreakdown}
                liveScore={liveScore}
                queuedCount={queuedCount}
                pendingAckCount={pendingAckCount}
                transitionError={transitionError}
                toastMessage={toast?.message}
                isRailLayout={isRailLayout}
                isPortraitStack={isPortraitStack}
                currentPhase={currentPhase}
                isExtraTime={periodInfo.isExtraTime}
                extraTimeSeconds={periodInfo.extraTimeSeconds}
                guardTransition={guardTransition}
                transitionToHalftime={transitionToHalftime}
                transitionToSecondHalf={transitionToSecondHalf}
                transitionToFulltime={transitionToFulltime}
                transitionToExtraFirst={transitionToExtraFirst}
                transitionToExtraHalftime={transitionToExtraHalftime}
                transitionToExtraSecond={transitionToExtraSecond}
                transitionToPenalties={transitionToPenalties}
                finishMatch={finishMatch}
                transitionDisabled={transitionDisabled}
                penaltiesTransitionDisabled={penaltiesTransitionDisabled}
                transitionReason={transitionReason}
                t={t}
              />
            )}
          </main>
        </div>

        <aside
          className="hidden xl:block border-l border-slate-800/80"
          data-testid="logger-shell-right"
          aria-hidden="true"
        />
      </div>

      {/* Substitution Flow Modal */}
      {showSubstitutionFlow && match && (
        <SubstitutionFlow
          matchId={matchId!}
          team={substitutionModalTeam ?? match.home_team}
          availablePlayers={substitutionAvailablePlayers}
          squadPlayers={substitutionSquadPlayers}
          onField={substitutionOnField}
          isSubmittingSquadPlayer={substitutionIsSubmittingSquadPlayer}
          squadSubmitError={substitutionSquadSubmitError}
          expelledPlayerIds={expelledPlayerIds}
          disciplinaryStatusByPlayer={cardDisciplinaryStatus}
          period={operatorPeriod}
          globalClock={globalClock}
          onSubmit={handleSubstitutionSubmit}
          onCancel={handleSubstitutionCancel}
          initialPlayerOn={substitutionInitialPlayerOn}
          initialPlayerOff={substitutionInitialPlayerOff}
        />
      )}

      {/* Cockpit-wide floating AI chat — desktop only (hover+fine pointer);
          overlays logger/analytics/review tabs. Touch gets the fixed shell
          inside AnalyticsView instead. PMA-COCK-179 (2026-09-10): hidden
          behind AI_INSIGHTS_ENABLED (default off, client request). */}
      {AI_INSIGHTS_ENABLED && matchId && (
        <FloatingAiChat matchId={matchId} t={t} />
      )}
    </div>
  );
}
